
import { defineComponent, ref, reactive, toRefs, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import f10Api from '@/api/f10ApiHk'
import Table from '@/components/table.vue'
import Charts from '@/components/page/charts.vue'
import Nodata from '@/components/page/nodata.vue'
import * as echarts from 'echarts'

// 指标定义
const metrics = [
  { label: '市值', value: 'marketValue' },
  { label: '总资产', value: 'totalAsset' },
  { label: '净利润', value: 'npParentCompanyOwners' },
  { label: '权益乘数', value: 'equityMultiplierDupont' },
  { label: '资产周转率', value: 'assetTRate' },
  { label: '资产负债率', value: 'debtAssetsRatio' },
  { label: 'ROE', value: 'roe' },
  { label: '股息率', value: 'dividendRatio' },
  { label: '营业收入', value: 'operatingRevenue' },
  { label: '营业净利润', value: 'netProfitRatio' }
];

// 区间定义
const intervalDefinitions = {
  marketValue: ['<10亿', '10亿~50亿', '50亿~100亿', '100亿~500亿', '500亿~1000亿', '1000亿~5000亿', '5000亿~10000亿', '≥10000亿'],
  totalAsset: ['<10亿', '10亿~50亿', '50亿~100亿', '100亿~500亿', '500亿~1000亿', '1000亿~5000亿', '5000亿~10000亿', '≥10000亿'],
  npParentCompanyOwners: ['<0', '0~5000万', '5000万～1亿', '1～5亿', '5～10亿', '10～50亿', '50～100亿', '≥100亿'],
  roe: ['<0', '0~5%', '5%～10%', '10%~15%', '15%～20%', '20%~25%', '25%~30%', '≥30%'],
  netProfitRatio: ['<0', '0~10%', '10%～15%', '15%~20%', '20%～30%', '30%~40%', '40%～50%', '≥50%'],
  dividendRatio: ['=0', '0~1%', '1%～3%', '3%~5%', '5%～8%', '8%~10%', '≥10%'],
  equityMultiplierDupont: ['<1.5', '1.5~2', '2~3', '3~5', '5~10', '10~15', '15~20', '≥20'],
  assetTRate: ['0~0.05', '0.05~0.1', '0.1~0.2', '0.2~0.4', '0.4~0.6', '0.6~0.8', '0.8~1', '≥1'],
  debtAssetsRatio: ['<10%', '10~30%', '30~50%', '50~70%', '70～80%', '80～90%', '90～95%', '≥95%'],
  operatingRevenue: ['<1亿', '1亿~5亿', '5亿~10亿', '10亿~50亿', '50亿~100亿', '100亿~500亿', '500亿~1000亿', '≥1000亿']
};

const baseColumns = [
  {
    title: '排名',
    dataIndex: 'rank',
    key: 'rank',
    width: '80px',
    align: 'center'
  },
  {
    title: '代码',
    dataIndex: 'symbol',
    key: 'symbol',
    width: '100px',
    align: 'left'
  },
  {
    title: '名称',
    dataIndex: 'name',
    key: 'name',
    width: '280px',
    ellipsis: true,
    align: 'left'
  }
];

export default defineComponent({
  props: {
    token: {
      type: String,
      default: () => ''
    },
    itemdata: {
      type: Object,
      default: () => ({})
    },
    pageSourceData: {
      type: Object,
      default: () => ({})
    },
    theme: {
      type: String,
      default: () => ''
    }
  },
  components: { Table, Charts, Nodata },
  setup(props) {
    const pageData: any = reactive({
      // 行业信息
      industryName: '资讯科技业-软件服务-数码解决方案服务',
      totalCompanies: 0,

      // 关键指标
      currentCompanyValue: 0,
      currentCompanyRank: 0,
      totalIndustryValue: 0,
      industryAvgValue: 0,
      industryMedianValue: 0,

      // 指标选择
      currentMetric: 'marketValue',
      metrics: metrics,

      // 图表数据
      option: null,
      intervalData: [],

      // 表格数据
      columns: [],
      data: [],
      currentData: [],
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,

      // 加载状态
      spinning: true
    });

    // 计算当前指标标签
    const currentMetricLabel = computed(() => {
      const metric = metrics.find(m => m.value === pageData.currentMetric);
      return metric ? metric.label : '';
    });

    // 判断是否应该显示总计和均值（市值、总资产、净利润、营业收入）
    const shouldShowTotalAndAvg = computed(() => {
      const totalAndAvgMetrics = ['marketValue', 'totalAsset', 'npParentCompanyOwners', 'operatingRevenue'];
      return totalAndAvgMetrics.includes(pageData.currentMetric);
    });

    // 格式化数值
    const formatValue = (value: number) => {
      if (value >= 1000000000) {
        return (value / 1000000000).toFixed(2) + '亿';
      } else if (value >= 10000) {
        return (value / 10000).toFixed(2) + '万';
      } else if (value < 1 && value > 0) {
        return (value * 100).toFixed(2) + '%';
      }
      return value ? value.toFixed(2) : '--';
    };

    // 计算行业中位数
    const calculateMedian = (values: number[]) => {
      if (!values || values.length === 0) return 0;

      const sortedValues = values.slice().sort((a, b) => a - b);
      const middle = Math.floor(sortedValues.length / 2);

      if (sortedValues.length % 2 === 0) {
        return (sortedValues[middle - 1] + sortedValues[middle]) / 2;
      } else {
        return sortedValues[middle];
      }
    };
    const route = useRoute()
    // 切换指标
    const changeMetric = () => {
      pageData.currentPage = 1;
      getInfoF10IndustryPlace();
    };

    // 更新分页数据
    const updatePagedData = () => {
      pageData.currentData = pageData.data;
    };

    // 设置行样式
    const setRowClassName = (record: any) => {
      return record.isCurrent ? 'highlight-row' : '';
    };

    // 更新图表 - 添加公司数量标签
    const updateChart = () => {
      if (!pageData.intervalData.length) return;

      const intervalLabels = intervalDefinitions[pageData.currentMetric];
      const values = pageData.intervalData;

      // 找到当前公司所在区间
      const currentInterval = pageData.data.find((item: any) => item.isCurrent)?.interval || 0;

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params: any) => {
            const dataIndex = params[0].dataIndex;
            return `<strong>${intervalLabels[dataIndex]}</strong><br/>公司数量: ${params[0].value}`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '10%',
          top: '15%', // 增加顶部空间给标签
          containLabel: true
        },
        // xAxis: {
        //   type: 'category',
        //   data: intervalLabels,
        //   axisLabel: {
        //     interval: 0,
        //     rotate: 0,
        //     fontSize: 12,
        //     // color: '#f8f8fa'
        //   },


        // },
        xAxis: {
          type: 'category',
          data: intervalLabels,
          axisLine: { // 控制轴线颜色
            lineStyle: {
              color: '#f8f8fa'
            }
          },
          axisTick: { // 控制刻度线颜色
            lineStyle: {
              color: '#f8f8fa'
            }
          },
          axisLabel: {
            interval: 0,
            rotate: 0,
            fontSize: 12,
            color: '#f8f8fa' // 控制标签文字颜色
          }
        },
        yAxis: {
          type: 'value',
          name: '家数',
          axisLine: {
            lineStyle: {
              color: props.theme === 'dark' ? '#f8f8fa' : '#333333'
            }
          },
          axisTick: {
            lineStyle: {
              color: props.theme === 'dark' ? '#f8f8fa' : '#333333'
            }
          },
          axisLabel: {
            color: props.theme === 'dark' ? '#f8f8fa' : '#333333'
          },
          splitLine: {
            lineStyle: {
              color: props.theme === 'dark' ? '#666666' : '#cccccc'
            }
          }
        },
        series: [
          {
            name: '',
            type: 'bar',
            barWidth: '50%',
            itemStyle: {
              color: (params: any) => {
                return params.dataIndex + 1 === currentInterval
                  ? '#ff7f0e'
                  : '#1890ff';
              }
            },
            // 添加公司数量标签
            label: {
              show: true,
              position: 'top',
              formatter: '{c}家',
              color: props.theme == 'dark' ? '#FFFFFF' : '#333333',
              fontSize: 12,
              fontWeight: 'bold'
            },

            data: values
          }
        ]
      };

      pageData.option = option;
    };
    const getData = () => {
      const data = {
        data: {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-unnecessary-boolean-literal-compare
          // market: 'HK' || route.query.market,
          // // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-unnecessary-boolean-literal-compare
          // symbol: '00700' || route.query.symbol
          market: route.query.market || 'HK',
          symbol: route.query.code || '00700',
        },
        // @ts-ignore
        headers: { token: props.token || '3d5eff0d3c7ac7f3af30c337dcdf6944' }
      }

      f10Api.getBriefDetail(data)
        .then((res: any) => {
          pageData.industryName = res.data.companyInfoDetail.industryName;
        })
        .catch(console.error)
        .finally(() => pageData.spinning = false)

    }
    // 获取行业排名数据
    const getInfoF10IndustryPlace = () => {
      pageData.spinning = true;

      const requestData = {
        data: {


          market: route.query.market || 'HK',
          symbol: route.query.code || '00700',
          // market: 'HK',
          // symbol: '00700',
          indexCode: pageData.currentMetric
        },
        headers: {
          token: props.token || '3d5eff0d3c7ac7f3af30c337dcdf6944',
        }
      };

      f10Api.getBriefRankDetail(requestData)
        .then((res: any) => {
          if (res?.data) {
            processApiData(res.data);
          } else {
            pageData.data = [];
          }
          pageData.spinning = false;
        })
        .catch(() => {
          pageData.spinning = false;
        });
    };

    // 处理API数据
    const processApiData = (data: any) => {
      // 更新行业信息

      pageData.totalCompanies = data.list?.length || 0;

      // 更新当前公司信息
      const currentCompany = data.rank;
      pageData.currentCompanyValue = currentCompany.value;
      pageData.currentCompanyRank = currentCompany.rank;

      // 更新行业统计信息
      pageData.totalIndustryValue = data.totalValue;
      pageData.industryAvgValue = data.avgValue;

      // 计算行业中位数
      const values = data.list.map((item: any) => item.value).filter((value: any) => value !== null && value !== undefined);
      pageData.industryMedianValue = calculateMedian(values);

      // 处理区间数据
      const intervals = intervalDefinitions[pageData.currentMetric];
      pageData.intervalData = [];
      for (let i = 0; i < intervals.length; i++) {
        pageData.intervalData.push(data.intervalTotal[i + 1] || 0);
      }

      // 处理列表数据
      // pageData.data = data.list.map((item: any) => ({
      //   ...item,
      //   isCurrent: item.symbol === '00700' || route.query.symbol
      // }));
      pageData.data = data.list.map((item: any, index: number) => ({
        ...item,
        isCurrent: item.symbol === (route.query.code || '00700'),
        rank: index + 1  // 使用索引+1作为排名
      }))

      // 计算当前公司的排名（基于在列表中的索引位置 + 1）
      const currentCompanyIndex = pageData.data.findIndex((item: any) => item.isCurrent);
      if (currentCompanyIndex !== -1) {
        pageData.currentCompanyRank = currentCompanyIndex + 1;
      }

      pageData.totalItems = pageData.data.length;
      updatePagedData();

      // 更新表格列
      const currentMetricObj = metrics.find(m => m.value === pageData.currentMetric);
      const currentMetricName = currentMetricObj ? currentMetricObj.label : '';

      pageData.columns = baseColumns.concat([
        {
          title: currentMetricName,
          dataIndex: 'value',
          key: 'value',
          align: 'right',
          customRender: ({ text }: { text: number }) => formatValue(text)
        } as any
      ]);

      // 更新图表
      updateChart();
    };

    onMounted(() => {
      getData()
      getInfoF10IndustryPlace();
    });

    return {
      ...toRefs(pageData),
      currentMetricLabel,
      shouldShowTotalAndAvg,
      formatValue,
      changeMetric,
      setRowClassName
    };
  }
});
